// src/Api/SalaryCalculationApi.js

import { axiosMaster, axiosSalary } from "../api/axiosInstance";

/* =====================================================
   CUSTOM API ERROR
===================================================== */

class ApiError extends Error {
  constructor(message, { cause, status } = {}) {
    super(message);

    this.name = "ApiError";
    this.status = status ?? cause?.response?.status ?? null;

    if (cause) {
      this.cause = cause;
    }
  }
}

/* =====================================================
   GET GROUP ID
===================================================== */

const getGroupId = () => {
  try {
    const user = JSON.parse(
      localStorage.getItem("user") || "{}"
    );

    if (!user?.groupId) {
      throw new ApiError(
        "No groupId found. Please login again."
      );
    }

    return Number(user.groupId);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      "Unable to read user information."
    );
  }
};

/* =====================================================
   REQUEST WRAPPER
===================================================== */

const request = async (
  apiCall,
  errorMessage
) => {
  try {
    return await apiCall();
  } catch (error) {
    if (
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED"
    ) {
      throw error;
    }

    throw new ApiError(errorMessage, {
      cause: error,
    });
  }
};

/* =====================================================
   RESTAURANTS
===================================================== */

export const getRestaurants = async ({
  signal,
} = {}) => {
  const groupId = getGroupId();

  return request(
    async () => {
      const response =
        await axiosMaster.get(
          `/RestaurantMaster/GetByGroup?groupId=${groupId}`,
          { signal }
        );

      return response.data || [];
    },
    "Failed to load restaurants."
  );
};

/* =====================================================
   EMPLOYEES
===================================================== */

export const getEmployees = async ({
  signal,
} = {}) => {
  const groupId = getGroupId();

  return request(
    async () => {
      const response =
        await axiosMaster.get(
          `/EmployeeMaster/GetByGroup?groupId=${groupId}`,
          { signal }
        );

      return response.data || [];
    },
    "Failed to load employees."
  );
};

/* =====================================================
   EMPLOYEE PROFILE
===================================================== */

export const getEmployeeProfiles = async (
  restId,
  { signal } = {}
) => {
  const groupId = getGroupId();

  if (!restId) {
    throw new ApiError(
      "Restaurant ID is required."
    );
  }

  return request(
    async () => {
      const response =
        await axiosSalary.get(
          `/EmployeeProfile/GetByGroup?groupId=${groupId}&restId=${restId}`,
          { signal }
        );

      return response.data || [];
    },
    "Failed to load employee profiles."
  );
};

/* =====================================================
   MONTHLY ATTENDANCE
===================================================== */

export const getMonthlyAttendance = async ({
  restId,
  month,
  year,
  signal,
} = {}) => {
  const groupId = getGroupId();

  if (!restId) {
    throw new ApiError(
      "Restaurant ID is required."
    );
  }

  if (!month) {
    throw new ApiError(
      "Month is required."
    );
  }

  if (!year) {
    throw new ApiError(
      "Year is required."
    );
  }

  return request(
    async () => {
      const response =
        await axiosSalary.get(
          `/MonthlyAttendanceSummary/GetByRestaurant?groupId=${groupId}&restId=${restId}&month=${month}&year=${year}`,
          { signal }
        );

      return response.data || [];
    },
    "Failed to load monthly attendance."
  );
};

/* =====================================================
   EXPORTS
===================================================== */

/* =====================================================
   ATTENDANCE POLICY
===================================================== */

export const getAttendancePolicy = async (
  restId,
  { signal } = {}
) => {
  const groupId = getGroupId();

  if (!restId) {
    throw new ApiError(
      "Restaurant ID is required."
    );
  }

  return request(
    async () => {
      const response =
        await axiosSalary.get(
          `/AttendancePolicyMaster/GetByGroupRestId?groupId=${groupId}&restId=${restId}`,
          { signal }
        );

      return response.data || null;
    },
    "Failed to load attendance policy."
  );
};

export { ApiError };